import React, { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Activity, PieChart as PieIcon, TrendingUp } from "lucide-react";
import { Badge, money, SectionCard } from "./FinanceReportUi";
import {
  buildAgingBuckets,
  buildDailyCashflowSeries,
  periodLabel,
} from "./financePeriodUtils";

const SOURCE_COLORS = ["#8b5cf6", "#2563eb", "#10b981", "#64748b", "#f59e0b"];
const AGING_COLORS = ["#10b981", "#3b82f6", "#f59e0b", "#ef4444"];

function formatCompact(value) {
  const n = Number(value) || 0;
  if (Math.abs(n) >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)} tỷ`;
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)} Tr`;
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(0)}k`;
  return String(Math.round(n));
}

function EmptyChart({ message }) {
  return (
    <div className="flex h-full min-h-[200px] items-center justify-center px-4 text-center text-[11px] font-black uppercase tracking-widest text-gray-300 sm:min-h-[260px] sm:text-xs">
      {message}
    </div>
  );
}

export function CashflowCharts({ data, filters, loading }) {
  const chartData = useMemo(() => {
    if (filters?.period_type === "month") {
      return buildDailyCashflowSeries(data?.ledger || []).map((d) => ({
        label: d.date.slice(8, 10),
        fullLabel: d.date,
        total_in: d.total_in,
        total_out: d.total_out,
        net: d.net,
      }));
    }

    const byMonth = data?.by_month || [];
    const map = new Map(byMonth.map((m) => [Number(m.month), m]));

    let months = [];
    if (filters?.period_type === "year") {
      months = Array.from({ length: 12 }, (_, i) => i + 1);
    } else if (filters?.period_type === "quarter") {
      const q = Number(filters.quarter) || 1;
      const start = (q - 1) * 3 + 1;
      months = [start, start + 1, start + 2];
    } else {
      months = byMonth.map((m) => Number(m.month));
    }

    return months.map((m) => {
      const hit = map.get(m);
      return {
        label: `T${m}`,
        fullLabel: `Tháng ${m}`,
        total_in: hit?.total_in || 0,
        total_out: hit?.total_out || 0,
        net: hit?.net || 0,
      };
    });
  }, [data, filters?.period_type, filters?.quarter]);

  const hasData = chartData.some(
    (d) => d.total_in || d.total_out || d.net
  );

  return (
    <div className="grid gap-4 xl:grid-cols-5">
      <div className="xl:col-span-3">
        <SectionCard
          title="Dòng tiền thu – chi – net"
          countLabel={periodLabel(filters)}
          action={
            <Badge tone="blue">
              <TrendingUp size={11} /> Cashflow
            </Badge>
          }
        >
          <div className="h-[240px] p-2 sm:h-[320px] sm:p-4">
            {loading ? (
              <EmptyChart message="Đang tải biểu đồ..." />
            ) : !hasData ? (
              <EmptyChart message="Chưa có dữ liệu dòng tiền" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 8, right: 4, left: -8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 10, fill: "#94a3b8", fontWeight: 700 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: "#94a3b8", fontWeight: 700 }}
                    axisLine={false}
                    tickLine={false}
                    width={40}
                    tickFormatter={formatCompact}
                  />
                  <Tooltip
                    formatter={(value, name) => {
                      if (name === "total_in") return [money(value), "Thu"];
                      if (name === "total_out") return [money(value), "Chi"];
                      return [money(value), "Net"];
                    }}
                    labelFormatter={(_, payload) =>
                      payload?.[0]?.payload?.fullLabel || ""
                    }
                  />
                  <Legend
                    formatter={(value) => {
                      if (value === "total_in") return "Thu";
                      if (value === "total_out") return "Chi";
                      return "Net";
                    }}
                  />
                  <Bar dataKey="total_in" fill="#10b981" radius={[6, 6, 0, 0]} maxBarSize={22} />
                  <Bar dataKey="total_out" fill="#ef4444" radius={[6, 6, 0, 0]} maxBarSize={22} />
                  <Line
                    type="monotone"
                    dataKey="net"
                    stroke="#2563eb"
                    strokeWidth={3}
                    dot={{ r: 3, fill: "#2563eb" }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>
        </SectionCard>
      </div>

      <div className="xl:col-span-2">
        <SectionCard
          title="Cơ cấu thu vs chi"
          countLabel="Tỷ trọng"
          action={
            <Badge tone="violet">
              <PieIcon size={11} /> Mix
            </Badge>
          }
        >
          <div className="h-[240px] p-2 sm:h-[320px] sm:p-4">
            {loading ? (
              <EmptyChart message="Đang tải..." />
            ) : !data?.summary ||
              (!(Number(data.summary.total_in) || Number(data.summary.total_out))) ? (
              <EmptyChart message="Chưa có dữ liệu" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={[
                      { name: "Thu", value: Number(data.summary.total_in) || 0 },
                      { name: "Chi", value: Number(data.summary.total_out) || 0 },
                    ]}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={58}
                    outerRadius={90}
                    paddingAngle={3}
                  >
                    <Cell fill="#10b981" />
                    <Cell fill="#ef4444" />
                  </Pie>
                  <Tooltip formatter={(v, n) => [money(v), n]} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </SectionCard>
      </div>
    </div>
  );
}

export function CollectionsCharts({ data, filters, loading }) {
  const sourceData = useMemo(
    () =>
      (data?.receipt_summary || []).map((r) => ({
        name: r.payer_type_label,
        value: Number(r.total) || 0,
        count: r.count,
      })),
    [data]
  );

  const aging = useMemo(
    () => buildAgingBuckets(data?.unpaid_invoices || []),
    [data]
  );

  return (
    <div className="grid gap-4 xl:grid-cols-2">
      <SectionCard
        title="Cơ cấu nguồn thu"
        countLabel={periodLabel(filters)}
        action={
          <Badge tone="emerald">
            <Activity size={11} /> Sources
          </Badge>
        }
      >
        <div className="h-[220px] p-2 sm:h-[300px] sm:p-4">
          {loading ? (
            <EmptyChart message="Đang tải..." />
          ) : sourceData.length === 0 ? (
            <EmptyChart message="Chưa có phiếu thu" />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={sourceData} layout="vertical" margin={{ left: 0, right: 8 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                <XAxis
                  type="number"
                  tickFormatter={formatCompact}
                  tick={{ fontSize: 10, fill: "#94a3b8", fontWeight: 700 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={72}
                  tick={{ fontSize: 10, fill: "#64748b", fontWeight: 700 }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  formatter={(v, _n, p) => [
                    `${money(v)} (${p?.payload?.count || 0} phiếu)`,
                    "Thu",
                  ]}
                />
                <Bar dataKey="value" radius={[0, 8, 8, 0]} maxBarSize={18}>
                  {sourceData.map((_, i) => (
                    <Cell key={i} fill={SOURCE_COLORS[i % SOURCE_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </SectionCard>

      <SectionCard
        title="Aging công nợ (AR)"
        countLabel="Theo ngày treo"
        action={<Badge tone="amber">Aging</Badge>}
      >
        <div className="h-[220px] p-2 sm:h-[300px] sm:p-4">
          {loading ? (
            <EmptyChart message="Đang tải..." />
          ) : aging.every((b) => b.amount === 0) ? (
            <EmptyChart message="Không có công nợ treo" />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={aging} margin={{ top: 8, right: 4, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10, fill: "#94a3b8", fontWeight: 700 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tickFormatter={formatCompact}
                  tick={{ fontSize: 10, fill: "#94a3b8", fontWeight: 700 }}
                  axisLine={false}
                  tickLine={false}
                  width={40}
                />
                <Tooltip
                  formatter={(v, _n, p) => [
                    `${money(v)} · ${p?.payload?.count || 0} HĐ`,
                    "Công nợ",
                  ]}
                />
                <Bar dataKey="amount" radius={[8, 8, 0, 0]} maxBarSize={36}>
                  {aging.map((_, i) => (
                    <Cell key={i} fill={AGING_COLORS[i % AGING_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </SectionCard>
    </div>
  );
}
